type ArticleMetadata = { title: string; description?: string; twitterTitle?: string };

// Search-only edits; article headings, body copy and JSON-LD stay unchanged.
const articleOverrides: Record<string, Partial<ArticleMetadata>> = {
  "the-hidden-energy-cost-of-ai-sustainability-lessons-from-salesforce": {
    "title": "AI Energy Costs: Sustainability Lessons from Salesforce"
  },
  "who-signs-off-your-salesforce-deal-and-why-it-is-not-your-ae": {
    "title": "Who Approves Your Salesforce Deal? It Is Not Your AE"
  },
  "unlocking-leverage-in-revenue-cloud-renewals-a-guide-for-enterprise-cfos": {
    "title": "Revenue Cloud Renewals: Leverage for Enterprise CFOs"
  },
  "most-salesforce-value-is-already-in-your-contract": {
    "title": "Salesforce Negotiations: A 3-Part Strategic Guide",
    "description": "Part one of the Salesforce negotiation guide covers renewal timing, shelfware audits, roadmap ownership and the people approving your deal."
  },
  "salesforce-s-new-core-advanced-and-max-editions-what-changed-and-what-it-costs": {
    "title": "Salesforce Core, Advanced & Max Editions: Changes and Costs",
    "twitterTitle": "Salesforce Core, Advanced & Max: Changes and Costs"
  },
  "why-nordic-enterprises-hesitate-external-consultants": {
    "title": "Why Nordic Firms Avoid Salesforce Procurement Advisors",
    "description": "Why Nordic enterprises run Salesforce renewals in-house: confidentiality concerns, switching myths, risk aversion and the commercial trade-offs."
  },
  "roadmap-software-spend-optimisation": {
    "title": "Build Your Licence Roadmap for Software Spend Success",
    "twitterTitle": "Build a Roadmap for Software Spend Success"
  },
  "from-chatbots-to-digital-workers-ai-agent-trends": {
    "description": "See how Agentforce is evolving from chatbots to digital workers, and what procurement teams should consider when mapping AI use cases to enterprise licences."
  },
  "how-salesforce-pricing-changes-across-products-and-contracts": {
    "title": "How Salesforce Pricing Varies by Product and Contract"
  },
  "salesforce-sela-vs-aela-navigating-the-agentic-ai-cost-paradigm": {
    "title": "Salesforce SELA vs. AELA: Agentic AI Costs",
    "twitterTitle": "Salesforce SELA vs. AELA: Managing AI Costs"
  },
  "salesforce-mid-contract-reductions-can-you-actually-scale-down": {
    "title": "Salesforce Mid-Contract Reductions: Can You Scale Down?",
    "twitterTitle": "Can You Reduce Salesforce Spend Mid-Contract?"
  },
  "how-to-assess-salesforce-strategic-partnerships-before-signing": {
    "title": "Assess Salesforce Strategic Partnerships Before Signing",
    "twitterTitle": "Assess Salesforce Partnerships Before Signing"
  },
  "salesforce-agentforce-cost-breakdown-how-flex-credits-and-tokens-impact-your-bill": {
    "title": "Salesforce Agentforce Costs: Flex Credits and Tokens"
  },
  "salesforce-sela-avoiding-bundling-traps-and-securing-decoupled-pricing": {
    "title": "Salesforce SELA: Avoid Bundling, Secure Decoupled Pricing",
    "twitterTitle": "Salesforce SELA: Avoid Bundling and Secure Better Pricing"
  },
  "inside-phmgs-salesforce-renewal-a-conversation-with-cfo-matthew-toynton": {
    "title": "PHMG's Salesforce Renewal: CFO Matthew Toynton Interview"
  },
  "salesforce-commercial-structures-standard-agreement-vs-sela-vs-aela": {
    "title": "Salesforce Contracts: Standard Agreement vs. SELA vs. AELA",
    "twitterTitle": "Salesforce Contracts: Standard, SELA or AELA?"
  },
  "saas-shelfware-the-unused-license-epidemic-and-how-to-stop-it": {
    "title": "SaaS Shelfware: How to Stop Unused Licences"
  },
  "salesforce-marketing-cloud-capabilities-enterprise-fit-and-commercial-traps": {
    "title": "Salesforce Marketing Cloud: Capabilities & Commercial Traps",
    "twitterTitle": "Salesforce Marketing Cloud: Fit, Features and Cost Traps"
  },
  "salesforce-multi-cloud-strategy-core-infrastructure-vs-unnecessary-skus": {
    "title": "Salesforce Multi-Cloud: Core Infrastructure vs. Excess SKUs",
    "twitterTitle": "Salesforce Multi-Cloud: Core Needs vs. Excess SKUs"
  },
  "the-blueprint-for-salesforce-aela-what-enterprise-buyers-need-to-know": {
    "title": "Salesforce AELA: A Guide for Enterprise Buyers"
  },
  "salesforce-marketing-cloud-supermessages-how-multipliers-and-channel-costs-work": {
    "title": "Marketing Cloud Supermessages: Multipliers & Channel Costs",
    "twitterTitle": "Marketing Cloud Supermessages: Multipliers and Costs"
  },
  "salesforce-agentforce-what-enterprise-leaders-actually-need-to-know": {
    "title": "Salesforce Agentforce: What Enterprise Leaders Need to Know"
  },
  "7-saas-contract-clauses-that-drive-up-salesforce-costs-part-2": {
    "title": "7 SaaS Contract Clauses Raising Salesforce Costs: Part 2",
    "twitterTitle": "7 SaaS Clauses That Raise Salesforce Costs: Part 2"
  },
  "navigating-the-salesforce-ecosystem-advisory-implementation-partners-and-hidden-costs": {
    "title": "Salesforce Ecosystem: Partners, Advisory & Hidden Costs",
    "twitterTitle": "Salesforce Partners: Advisory, Implementation and Costs"
  },
  "how-different-salesforce-licenses-affect-your-renewal-budget": {
    "title": "How Salesforce Licences Affect Your Renewal Budget"
  },
  "software-renewals-that-need-attention-earlier-than-you-think": {
    "title": "Software Renewals That Need Early Attention"
  },
  "a-smarter-salesforce-strategy-starts-with-commercial-clarity": {
    "title": "Salesforce Strategy Starts With Commercial Clarity"
  },
  "salesforce-data360-data-cloud-optimization-avoiding-the-multi-million-dollar-storage-trap": {
    "title": "Salesforce Data Cloud (Data 360): Avoid Storage Cost Traps",
    "twitterTitle": "Salesforce Data 360: Avoiding Costly Storage Traps"
  },
  "how-agentic-enterprise-licence-agreement-pricing-really-works": {
    "title": "How Agentic Enterprise Licence Agreement Pricing Works"
  }
};

export function articleSearchMetadata(slug: string, title: string, description: string): ArticleMetadata {
  const override = articleOverrides[slug];
  const brandedTitle = `${title} — SaaSed`;
  const sourceTitle = override?.title ?? (brandedTitle.length < 60 ? brandedTitle : title);
  const shortTitle = sourceTitle.length < 60 || (override?.title && sourceTitle.length <= 60) ? sourceTitle : `${sourceTitle.slice(0, 58).replace(/\s+\S*$/, "").trimEnd()}…`;
  const sourceDescription = override?.description ?? description;
  const shortDescription = sourceDescription.length > 160
    ? `${sourceDescription.slice(0, 159).replace(/\s+\S*$/, "").trimEnd()}…`
    : sourceDescription;
  return {
    title: shortTitle,
    ...(override?.twitterTitle ? { twitterTitle: override.twitterTitle } : {}),
    description: shortDescription.length >= 25 ? shortDescription : `Salesforce procurement insights: ${shortTitle}`,
  };
}
